// Shared pluralization utilities for consistent entity type labeling across the application

// Label object structure for singular and plural forms
export interface EntityLabel {
    singular: string;
    plural: string;
}

// Known type to label mapping for proper humanization
// Do not use plural in the keys
export const typeToLabelMap: Record<string, EntityLabel> = {
    'ApiUser': { singular: 'Api User', plural: 'Api Users' },
    'AppRegistration': { singular: 'App Registration', plural: 'App Registrations' },
    'Applicant': { singular: 'Applicant', plural: 'Applicants' },
    'Base': { singular: 'Base Resource', plural: 'Base Resources' },
    'Building': { singular: 'Building', plural: 'Buildings' },
    'Class': { singular: 'Class', plural: 'Classes' },
    'Content': { singular: 'Content', plural: 'Contents' },
    'Contractor': { singular: 'Contractor', plural: 'Contractors' },
    'Deleted': { singular: 'Deleted', plural: 'Deletions' },
    'EmailTemplate': { singular: 'Email Template', plural: 'Email Templates' },
    'Employee': { singular: 'Employee', plural: 'Employees' },
    'FunctionCatalog': { singular: 'Function Catalog', plural: 'Function Catalogs' },
    'Group': { singular: 'Group', plural: 'Groups' },
    'Image': { singular: 'Image', plural: 'Images' },
    'JournalEntries': { singular: 'Journal Entry', plural: 'Journal Entries' },
    'ModificationError': { singular: 'Modification Error', plural: 'Modification Errors' },
    'PerpetualAccess': { singular: 'Perpetual Access', plural: 'Perpetual Access' },
    'Person': { singular: 'Person', plural: 'Persons' },
    'Photo': { singular: 'Photo', plural: 'Photos' },
    'PluginFunction': { singular: 'Plug-in Function', plural: 'Plug-in Functions' },
    'PluginFunctionType': { singular: 'Plug-in Function Type', plural: 'Plug-in Function Types' },
    'PolicyRule': { singular: 'Policy Rule', plural: 'Policy Rules' },
    'Public': { singular: 'Public User', plural: 'Public Users' },
    'RcJobCode': { singular: 'RC Job Code', plural: 'RC Job Codes' },
    'RequestApproval': { singular: 'Request Approval', plural: 'Request Approvals' },
    'RequestApprovalType': { singular: 'Request Approval Type', plural: 'Request Approval Types' },
    'ResponsibilityCenter': { singular: 'Responsibility Center', plural: 'Responsibility Centers' },
    'SchemaAttribute': { singular: 'Schema Attribute', plural: 'Schema Attributes' },
    'SchemaBinding': { singular: 'Schema Binding', plural: 'Schema Bindings' },
    'SchemaEntity': { singular: 'Schema Entity', plural: 'Schema Entities' },
    'School': { singular: 'School', plural: 'Schools' },
    'ScopedDomainController': { singular: 'Scoped Domain Controller', plural: 'Scoped Domain Controllers' },
    'Set': { singular: 'Set', plural: 'Sets' },
    'SetTransition': { singular: 'Set Transition', plural: 'Set Transitions' },
    'SetTransitionEvent': { singular: 'Set Transition Event', plural: 'Set Transition Events' },
    'Shared': { singular: 'Shared User', plural: 'Shared Users' },
    'SpooledEmail': { singular: 'Spooled Email', plural: 'Spooled Emails' },
    'Sponsored': { singular: 'Sponsored User', plural: 'Sponsored Users' },
    'Student': { singular: 'Student', plural: 'Students' },
    'StudentBusStop': { singular: 'Student Bus Stop', plural: 'Student Bus Stops' },
    'StudentTransportationNeed': { singular: 'Student Transportation Need', plural: 'Student Transportation Needs' },
    'Vacancy': { singular: 'Vacancy', plural: 'Vacancies' },
    'WeekdayAccess': { singular: 'Weekday Access', plural: 'Weekday Access' },
    'WeekendAccess': { singular: 'Weekend Access', plural: 'Weekend Access' },
    'Workflow': { singular: 'Workflow', plural: 'Workflows' },
    'WorkflowActivity': { singular: 'Workflow Activity', plural: 'Workflow Activities' }
};

/**
 * Find a label entry by case-insensitive key match
 */
function findLabelEntry(typeName: string): EntityLabel | undefined {
    // Try exact match first
    if (typeToLabelMap[typeName]) {
        return typeToLabelMap[typeName];
    }

    // Try case-insensitive match
    const lowerTypeName = typeName.toLowerCase();
    for (const [key, value] of Object.entries(typeToLabelMap)) {
        if (key.toLowerCase() === lowerTypeName) {
            return value;
        }
    }

    return undefined;
}

/**
 * Find a label entry by checking if the typeName matches either singular or plural form
 * Handles both humanized forms (with spaces) and camelCase/PascalCase forms
 */
function findLabelEntryByValue(typeName: string): { key: string; label: EntityLabel } | undefined {
    const lowerTypeName = typeName.toLowerCase();
    // Remove spaces from the input for comparison (handles humanized forms)
    const normalizedInput = lowerTypeName.replace(/\s+/g, '');

    for (const [key, label] of Object.entries(typeToLabelMap)) {
        // Check against humanized forms (with spaces)
        const singularLower = label.singular.toLowerCase();
        const pluralLower = label.plural.toLowerCase();

        // Check against camelCase/PascalCase forms (remove spaces)
        const singularNormalized = singularLower.replace(/\s+/g, '');
        const pluralNormalized = pluralLower.replace(/\s+/g, '');

        // Also check the key itself (camelCase/PascalCase)
        const keyLower = key.toLowerCase();

        if (singularLower === lowerTypeName ||
            pluralLower === lowerTypeName ||
            singularNormalized === normalizedInput ||
            pluralNormalized === normalizedInput ||
            keyLower === lowerTypeName) {
            return { key, label };
        }
    }
    return undefined;
}

/**
 * Get the proper pluralized label for an entity type
 * @param typeName - The entity type name (e.g., 'Class', 'Employee') - should be singular
 * @returns The properly pluralized label (e.g., 'Classes', 'Employees')
 */
export const getPluralizedLabel = (typeName: string): string => {
    const label = findLabelEntry(typeName);
    if (label) {
        return label.plural;
    }

    // Try reverse lookup by singular/plural value
    const entry = findLabelEntryByValue(typeName);
    if (entry) {
        return entry.label.plural;
    }

    // Fallback to adding 's'
    return `${typeName}s`;
};

/**
 * Get the proper singular label for an entity type
 * @param typeName - The entity type name (e.g., 'StudentTransportationNeed', 'Classes', 'Employees')
 * @returns The properly singularized label (e.g., 'Student Transportation Need', 'Class', 'Employee')
 */
export const getSingularLabel = (typeName: string): string => {
    const label = findLabelEntry(typeName);
    if (label) {
        return label.singular;
    }

    // Try reverse lookup by singular/plural value
    const entry = findLabelEntryByValue(typeName);
    if (entry) {
        return entry.label.singular;
    }

    // Fallback: format the type name nicely
    return formatSingularLabel(typeName);
};

/**
 * Humanize a string by breaking apart on capital letters with spaces
 * Preserves the original case of the string
 * @param str - The string to humanize (e.g., 'XmlFunction' or 'xmlFunction')
 * @returns The humanized string (e.g., 'Xml Function' or 'xml function')
 * 
 * @example
 * humanize('XmlFunction') // Returns 'Xml Function'
 * humanize('xmlFunction') // Returns 'xml function'
 * humanize('StudentTransportationNeed') // Returns 'Student Transportation Need'
 */
export const humanize = (str: string): string => {
    if (!str) return str;
    return str
        .replace(/([A-Z])/g, ' $1') // Add space before capital letters
        .trim();
};

/**
 * Format a singular label from an entity type name
 * Converts camelCase/PascalCase to Title Case with spaces
 */
function formatSingularLabel(typeName: string): string {
    return typeName
        .replace(/([A-Z])/g, ' $1') // Add space before capital letters
        .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
        .trim();
}
